import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { InlineAlert } from '@rbilabs/components-library';
import { Icon } from '@rbilabs/components-library/build/components/icon';
import { Tab, TabList } from '@rbilabs/components-library/build/components/tabs';
import { isEmpty } from 'lodash';
import { useIntl } from 'react-intl';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useLocation, useMatch, useNavigate } from 'react-router';

import { Tile } from 'components/features/components/marketing-tile-group/marketing-tile-group';
import { HorizontalPosition, TileSize } from 'components/marketing-tile';
import { ModalQuestUnlockedIncentive } from 'components/modal-quest-unlocked-incentive';
import { LoyaltyTierKey } from 'generated/graphql-gateway';
import { useFavoriteOffers } from 'hooks/favorite-offers';
import { useBatteryLevel } from 'hooks/use-battery-level';
import { useOffersPageMarketingTiles } from 'hooks/use-combine-offers-and-marketing-tiles';
import { useEffectOnceOnCondition } from 'hooks/use-effect-once-on-condition';
import { useEnableGreyedOutOffers } from 'hooks/use-enable-greyed-out-offers';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { useLoyaltyExternalOffers } from 'hooks/use-loyalty-external-offers';
import { useLoyaltyExternalOffersEmptyStates } from 'hooks/use-loyalty-external-offers-empty-states';
import { useLoyaltyOffersMarketingTiles } from 'hooks/use-loyalty-offers-marketing-tiles';
import { MediaQuery, useMediaQuery } from 'hooks/use-media-query';
import { useThrottle } from 'hooks/use-throttle';
import { IncentiveCardSkeleton } from 'pages/loyalty/loyalty-incentives-components/incentive-card/incentive-card-skeleton';
import { OffersEmptyState } from 'pages/loyalty/loyalty-offers/offers-empty-state';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EVENT_PAGE_NAMES, EventTypes } from 'state/cdp/constants';
import { useGeolocation } from 'state/geolocation';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useLoyaltyContext } from 'state/loyalty';
import { IncentiveEvaluationMap } from 'state/loyalty/hooks/types';
import { useLoyaltyQuests } from 'state/loyalty/hooks/use-loyalty-quests';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';
import {
  ICartEntryType,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import { ExternalOffer, LoyaltyOffer } from 'state/loyalty/types';
import { isDiscountLoyaltyOffer, isTestOnly, isUpcomingOffer } from 'state/loyalty/utils';
import { useNearbyStoresContext } from 'state/nearby-stores';
import { useStoreNearby } from 'state/nearby-stores/hooks';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { EventName, emitEvent } from 'utils/event-hub';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import {
  PerformancePages,
  initializePagePerformance,
  pageLoaded,
  pageUnloaded,
  performanceReport,
} from 'utils/render-performance';
import { routes } from 'utils/routing';
import { toast } from 'utils/toast';

import { IncentiveItemsContainer, LoyaltyIncentivesGrid } from '../loyalty-incentives-components';
import { ExternalIncentiveCard } from '../loyalty-incentives-components/external-incentive-card/external-incentive-card';
import { ExternalIncentiveCardDescription } from '../loyalty-incentives-components/external-incentive-card/external-incentive-card.styled';
import { IncentiveCard } from '../loyalty-incentives-components/incentive-card/incentive-card';
import { IncentiveCardDescription } from '../loyalty-incentives-components/incentive-card/incentive-card.styled';
import { parseIncentiveData } from '../loyalty-incentives-components/incentive-card/parse-incentive-data';
import { useLoyaltyIncentivesAvailability } from '../loyalty-incentives-components/use-loyalty-incentives-availability';

import { LoyaltyEndOfOffers } from './end-of-offers/loyalty-end-of-offers';
import { ExternalOffersEmptyState } from './external-offers-empty-state';
import { FavoritesEmptyState } from './favorite-empty-state';
import {
  LoyaltyIncentivesContainer,
  OffersSkeletonPromoCode,
  OffersSkeletonTabs,
  TabContainer,
  TabDisclaimer,
} from './loyalty-offers.styled';
import { LoyaltyPromoCode } from './loyalty-promo-code';
import { OffersInCart } from './offers-in-cart/offers-in-cart';
import { AvailableTabs, AvailableTabsType, OfferStatus } from './types';
import { getSelectedIncentive, isGreyedOutOffer, reduceOffers } from './utils';

type TLocationState = { selectedTab?: AvailableTabsType; backRoute?: string } | undefined;

const LoyaltyOffersBase = () => {
  // Track page loading performance
  initializePagePerformance(PerformancePages.OFFERS_PAGE);

  const { formatMessage } = useIntl();
  const location = useLocation();
  const state = location.state as TLocationState;
  const { user } = useAuthContext();
  const { loyaltyUser } = useLoyaltyUser();
  const { queryLoyaltyOffers, queryLoyaltyUserStandardOffers, refetchUserOffers } =
    useLoyaltyContext();
  const { isFavorite, toggleFavorite, isOfferLoading } = useFavoriteOffers();
  const loyaltyCmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);
  const userOffers = useAppSelector(selectors.loyalty.selectUserOffers);
  const unauthenticatedOffers = useAppSelector(selectors.loyalty.selectOffers);
  const personalizedOffers = useAppSelector(selectors.loyalty.selectPersonalizedOffers);
  const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);
  const dispatch = useAppDispatch();
  const { inRestaurantRedemptionCart, removeInRestaurantRedemptionEntry } =
    useInRestaurantRedemptionContext();
  const enableSortOffersForServiceMode = useFlag(
    LaunchDarklyFlag.ENABLE_SORT_OFFERS_FOR_SERVICE_MODE
  );
  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);
  const enableLoyaltyPromoCodes = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_PROMO_CODES);
  const enableGeolocation = useFlag(LaunchDarklyFlag.ENABLE_SETTINGS_GEOLOCATION);
  const { removeFromCart, ...order } = useOrderContext();
  const { address1: addressOfNearestStore, restaurantGroup } = useNearbyStoresContext();
  const { noStoreSelected, restaurantLogAttributesRef } = useStoreContext();
  const { isAuthenticated } = useAuthContext();
  const { enableInvitationCode } = useFeatureInvitationCode();
  const { marketingTiles } = useLoyaltyOffersMarketingTiles();
  const { externalOffersEmptyStates: enableExternalOffersEmptyState } =
    useLoyaltyExternalOffersEmptyStates();
  const { externalOffers: originalExternalOffers } = useLoyaltyExternalOffers();
  // Enable External Offers only when the Live External Offers document is published in Sanity
  const enableExternalOffers =
    originalExternalOffers || enableExternalOffersEmptyState ? true : false;
  const userIsAuthenticated = isAuthenticated();

  const { isPermissionGranted } = useGeolocation();
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const { enableGreyedOutOffers } = useEnableGreyedOutOffers(userIsAuthenticated);
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const navigate = useNavigate();
  const { setEventPageName } = useLocationContext();
  const { params } =
    useMatch({
      path: `${formatMessage({ id: 'routes.loyaltyHome' })}/:tab/*`,
    }) || {};

  const loyaltyEngineId = params?.['*'];
  const tabPath = params?.tab! || '';
  const cdp = useCdpContext();
  const { selectFirstNearbyStore } = useStoreNearby();
  const { loyaltyTierKey: userTier } = useLoyaltyUserTier();

  // External Offers can be accessible for not authenticated Users
  const dynamicAvailableTabs = useMemo(() => {
    if (enableExternalOffers) {
      return userIsAuthenticated
        ? { AllOffers: 0, ExternalOffers: 1, Favorites: 2 }
        : { AllOffers: 0, ExternalOffers: 1 };
    }
    return { AllOffers: 0, Favorites: 1 };
  }, [enableExternalOffers, userIsAuthenticated]);

  const [selectedTab, setSelectedTab] = useState<AvailableTabs>(
    dynamicAvailableTabs[state?.selectedTab ?? 'AllOffers'] as AvailableTabs
  );

  const {
    challengeIncentives,
    onChallengeIcentiveSelect,
    challengeIncentiveSelected,
    questsLoading,
    onChangeUnlockedIncentivesModalVisibilityStatus,
    unlockedIncentivesModalVisibilityStatus,
  } = useLoyaltyQuests(loyaltyUser?.id ?? '');

  const offerUpdatedMessage = useMemo(() => {
    return restaurantGroup?.name
      ? formatMessage(
          { id: 'offersUpdatedByLocation' },
          {
            location: restaurantGroup?.name,
          }
        )
      : formatMessage({ id: 'offersUpdated' });
  }, [formatMessage, restaurantGroup]);

  const shouldRenderOfferUpdatedMessage = useMemo(
    () => enableGeolocation && noStoreSelected && (restaurantGroup?.name || addressOfNearestStore),
    [addressOfNearestStore, enableGeolocation, noStoreSelected, restaurantGroup]
  );

  // TODO: extract this method to a reusable place
  const removeLoyaltyOffer = (offerToRemove: LoyaltyOffer) => {
    const appliedOfferToRemove = appliedOffers.find(
      offer => offer.id === offerToRemove.loyaltyEngineId
    );
    if (appliedOfferToRemove?.cartId) {
      removeFromCart(appliedOfferToRemove);
      return true;
    }

    const redemptionOfferToRemove = inRestaurantRedemptionCart.find(
      cartEntry =>
        cartEntry.type === ICartEntryType.OFFER && cartEntry.details.offer._id === offerToRemove._id
    );
    if (redemptionOfferToRemove) {
      removeInRestaurantRedemptionEntry(redemptionOfferToRemove);
      return true;
    }

    return false;
  };

  const handleRemove = (offerToRemove: LoyaltyOffer) => {
    let showNotification = removeLoyaltyOffer(offerToRemove);

    if (isDiscountLoyaltyOffer(offerToRemove)) {
      dispatch(actions.loyalty.setSelectedOffer(null));
      dispatch(actions.loyalty.resetAppliedOffers());
      showNotification = true;
    }

    if (showNotification) {
      toast.success(formatMessage({ id: 'offerAppliedSuccess' }));
    }
  };

  const cartTotal: number = useMemo(
    () => order?.calculateCartTotal && order.calculateCartTotal(),
    [order]
  );

  let loyaltyId = loyaltyUser?.id;
  // Some markets want to use promo-code and have enabled loyalty flag only on the BE.
  // This logic permits use promo code, even when the loyalty flag is not enabled on the FE
  // When the loyalty flag is enabled on the BE, we can get the loyaltyId from the user data
  // instead of the loyaltyUser, which is only set when the loyalty FE flag is enabled
  if (enableLoyaltyPromoCodes) {
    loyaltyId = user?.loyaltyId ?? loyaltyUser?.id;
  }

  const fetchOffers = useCallback(() => {
    if (loyaltyId) {
      queryLoyaltyUserStandardOffers({
        loyaltyId,
        subtotalAmount: cartTotal,
        totalAmount: cartTotal,
      });
    } else {
      queryLoyaltyOffers({ subtotalAmount: cartTotal, totalAmount: cartTotal });
    }
  }, [cartTotal, loyaltyId, queryLoyaltyOffers, queryLoyaltyUserStandardOffers]);

  // Fetch offers on first mount
  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Refetch offers if route changes to base route
  useEffect(() => {
    const isBaseOffersRoute = !loyaltyEngineId;
    if (isBaseOffersRoute) {
      fetchOffers();
    }
  }, [fetchOffers, loyaltyEngineId]);

  const throttlePerformanceEvent = useThrottle(() => {
    cdp.trackEvent({
      name: CustomEventNames.LOYALTY_LOAD,
      type: EventTypes.Other,
      attributes: performanceReport(PerformancePages.OFFERS_PAGE),
    });
  }, 4000);

  useEffect(() => {
    initializePagePerformance(PerformancePages.OFFERS_PAGE);

    // If offers are still loading do nothing
    if (offersLoading) {
      return;
    }

    requestAnimationFrame(() => {
      // Page content loaded event
      pageLoaded(PerformancePages.OFFERS_PAGE);

      throttlePerformanceEvent();
    });

    return () => {
      pageUnloaded(PerformancePages.OFFERS_PAGE);
    };
  }, [offersLoading]);

  useEffectOnceOnCondition(
    () => {
      emitEvent(EventName.SCREEN_RENDER, {
        screenId: 'loyaltyOffers',
      });
    },
    () => !loyaltyEngineId
  );

  const userBatteryLevel = useBatteryLevel();

  const [offers, offersInCart] = useMemo(
    () =>
      !loyaltyCmsOffers
        ? []
        : reduceOffers({
            cmsOffers: loyaltyCmsOffers,
            personalizedOffers,
            baseOffers: loyaltyId ? userOffers : unauthenticatedOffers,
            appliedOffers,
            inRestaurantRedemptionCart,
            checkLoyaltyOfferAvailability,
            enableSortOffersForServiceMode,
            setOffersFeedbackMap: (offerFeedbackMap: IncentiveEvaluationMap) =>
              dispatch(actions.loyalty.setOffersFeedbackMap(offerFeedbackMap)),
            userOfferAttributes: {
              userBatteryLevel,
              dobDeleted: user?.details.dobDeleted,
              phoneNumberVerified: user?.details.phoneVerified,
              promotionalEmails: user?.details.promotionalEmails,
              zipcode: user?.details.zipcode,
              hasDeliveryAddress: !!user?.details.deliveryAddresses?.length,
              favoriteStores: user?.details.favoriteStores?.map(store => store.storeNumber),
              favoriteOffers: user?.details.favoriteOffers?.map(offer => offer.id),
            },
          }),
    [
      loyaltyCmsOffers,
      personalizedOffers,
      loyaltyId,
      userOffers,
      unauthenticatedOffers,
      appliedOffers,
      inRestaurantRedemptionCart,
      checkLoyaltyOfferAvailability,
      enableSortOffersForServiceMode,
      userBatteryLevel,
      user?.details,
      dispatch,
    ]
  );

  const externalOffers = originalExternalOffers as ExternalOffer[];

  const selectedIncentive = getSelectedIncentive(
    loyaltyEngineId,
    offers,
    offersInCart,
    isMobile,
    challengeIncentives,
    externalOffers
  );

  const { sortedTilesAndOffers, marketingTileTop, marketingTileBottom } =
    useOffersPageMarketingTiles({
      offers,
      offersPageMarketingTiles: marketingTiles,
      selectedOffer: selectedIncentive || null,
    });

  const onPromoCodeRedeemSuccess = (offer: LoyaltyOffer) => {
    toast.success(formatMessage({ id: 'promoCodeAddedToOffers' }));
    dispatch(actions.loyalty.setCmsOffers([offer]));
    dispatch(actions.loyalty.unshiftPersonalizedOffer(offer));
    fetchOffers();
  };

  const handleClick =
    ({
      incentiveName,
      loyaltyEngineId: loyaltyEngineIdProp,
      incentiveSanityId,
      isQuestChallengeIcentive,
      isExternalOffer,
      isUpcomingOffer,
      status,
    }: {
      incentiveName: string;
      loyaltyEngineId: string;
      incentiveSanityId: string;
      isQuestChallengeIcentive?: boolean;
      isExternalOffer?: boolean;
      isUpcomingOffer?: boolean;
      status?: OfferStatus;
    }) =>
    () => {
      if (incentiveSanityId !== selectedIncentive?._id) {
        const navigateID = isExternalOffer ? incentiveSanityId : loyaltyEngineIdProp;
        const eventName = isExternalOffer
          ? CustomEventNames.EXTERNAL_OFFER_SELECTED
          : CustomEventNames.OFFER_SELECTED;

        cdp.trackEvent({
          name: eventName,
          type: EventTypes.Other,
          attributes: {
            engineId: loyaltyEngineIdProp,
            sanityId: incentiveSanityId,
            name: incentiveName,
            status,
          },
        });
        if (isExternalOffer) {
          // Define the page name used in Page View event
          setEventPageName(EVENT_PAGE_NAMES.ExternalOffersDetail);
        }

        if (isUpcomingOffer) {
          setEventPageName(EVENT_PAGE_NAMES.UpcomingOfferDetail);
        }

        onChallengeIcentiveSelect(isQuestChallengeIcentive);
        navigate(`${routes.rewardsOffers}/${navigateID}`);
      }
    };

  const handleTabChange = useCallback(
    (event: React.ChangeEvent, value: number) => {
      event.preventDefault();
      const tabName = Object.keys(dynamicAvailableTabs)[value];
      cdp.logNavigationClick(CustomEventNames.BUTTON_CLICK, {
        name: tabName,
        ...restaurantLogAttributesRef.current,
      });
      setSelectedTab(value);
    },
    [cdp, dynamicAvailableTabs]
  );

  const handleRefetch = async () => {
    if (loyaltyId) {
      refetchUserOffers({
        loyaltyId,
      });
    }
    fetchOffers();
  };

  useEffect(() => {
    if (loyaltyEngineId && !selectedIncentive && !offersLoading && !isEmpty(offers)) {
      navigate(`${formatMessage({ id: 'routes.loyaltyHome' })}/${tabPath}`);

      toast.warning(
        formatMessage(
          { id: 'incentiveFeedbackInvalidStore' },
          { incentiveType: formatMessage({ id: 'offer' }) }
        )
      );
    }
  }, [formatMessage, loyaltyEngineId, navigate, offers, offersLoading, selectedIncentive, tabPath]);

  useEffect(() => {
    if (enableGeolocation) {
      selectFirstNearbyStore();
    }
  }, [enableGeolocation, selectFirstNearbyStore]);

  const shouldHideSelectedOffer =
    selectedTab === dynamicAvailableTabs.Favorites &&
    offersInCart.some(offer => !isFavorite(offer?.loyaltyEngineId || ''));

  const loadingNearbyStore = useMemo(
    () =>
      enableGeolocation
        ? isPermissionGranted && !addressOfNearestStore && addressOfNearestStore !== null
        : false,
    [addressOfNearestStore, enableGeolocation, isPermissionGranted]
  );

  // Array containing the engineId of offers used as upsell options
  const upsellOffers = offers?.reduce((acc: string[], offer) => {
    if (offer.upsellOptions?.length) {
      offer.upsellOptions.forEach(upsellOffer => acc.push(upsellOffer?.loyaltyEngineId!));
    }
    return acc;
  }, []);

  const offersAndTiles = sortedTilesAndOffers
    ?.map((offerOrTile: any, idx: number) => {
      const isOffer =
        offerOrTile?._type === 'systemwideOffer' || offerOrTile?._type === 'configOffer';

      // If we selected the Favorites tab we filter any non-favorited offer / tile
      // If we selected the External Offers tab we don't want to display any Loyalty Offer
      if (
        (enableFavoriteOffers &&
          selectedTab === dynamicAvailableTabs.Favorites &&
          !isFavorite(offerOrTile._id || '')) ||
        (enableExternalOffers && selectedTab === dynamicAvailableTabs.ExternalOffers) ||
        isTestOnly(offerOrTile)
      ) {
        return null;
      }

      const offerIsUpcoming = isUpcomingOffer(offerOrTile);

      if (isOffer) {
        if (upsellOffers.includes(offerOrTile?.loyaltyEngineId) || isTestOnly(offerOrTile)) {
          return null;
        }

        const incentiveData = parseIncentiveData(offerOrTile);

        return (
          <IncentiveCard
            key={offerOrTile.loyaltyEngineId || idx}
            incentiveData={incentiveData}
            handleClick={handleClick}
            locked={enableGreyedOutOffers && isGreyedOutOffer(offerOrTile, userIsAuthenticated)}
            favoriteEnabled={enableFavoriteOffers && userIsAuthenticated}
            favorited={enableFavoriteOffers && isFavorite(offerOrTile._id || '')}
            favoriteLoading={isOfferLoading(offerOrTile._id || '')}
            handleToggleFavorite={toggleFavorite}
            isUpcomingOffer={offerIsUpcoming}
          >
            {incentiveData.description && (
              <IncentiveCardDescription content={incentiveData.description} />
            )}
          </IncentiveCard>
        );
      }
      return (
        <Tile
          tile={offerOrTile}
          key={offerOrTile?._id}
          size={TileSize.HALF}
          buttonPosition={HorizontalPosition.RIGHT}
          hasBorder={false}
          additionalPadding={`0.5rem`}
        />
      );
    })
    .filter((item: any) => !!item);

  const availableExternalOffers = externalOffers?.map((offer: ExternalOffer, idx: number) => {
    // True or null will close the offer to authenticated users only
    const offerRequiresAuthentication =
      offer.authentificationRequired || offer.authentificationRequired === null;

    // If no loyalty tier is set for the offer,
    // then the default behaviour is the external offers showing for all Loyalty Tiers.
    const isOfferAvailableToUserTier =
      offer.tierIncentiveList?.some(tierIncentive => tierIncentive?.tierName === userTier) ||
      !offer.tierIncentiveList;

    // If the offer doesn't require authentication it always should be open
    const isOfferClosed =
      offerRequiresAuthentication && (!isAuthenticated() || !isOfferAvailableToUserTier);

    if (
      (enableFavoriteOffers &&
        selectedTab === dynamicAvailableTabs.Favorites &&
        !isFavorite(offer._id || '')) ||
      isTestOnly(offer)
    ) {
      return null;
    }

    const offerTier = offer.tierIncentiveList?.[0]?.tierName as LoyaltyTierKey;

    return (
      <ExternalIncentiveCard
        key={offer._id || idx}
        incentiveData={offer}
        handleClick={handleClick}
        favoriteEnabled={enableFavoriteOffers && isAuthenticated()}
        favorited={enableFavoriteOffers && isFavorite(offer._id || '')}
        favoriteLoading={isOfferLoading(offer._id || '')}
        handleToggleFavorite={toggleFavorite}
        isOfferClosed={isOfferClosed}
        offerTier={offerTier}
      >
        {offer.subtitle && (
          <ExternalIncentiveCardDescription>
            {offer.subtitle.locale}
          </ExternalIncentiveCardDescription>
        )}
      </ExternalIncentiveCard>
    );
  });

  const isExternalOffer = (offer: LoyaltyOffer | ExternalOffer | undefined) =>
    offer?.__typename === 'ExternalOffers';

  const favoritesEmptyState =
    !sortedTilesAndOffers?.some(offer => isFavorite(offer._id)) &&
    !externalOffers?.some((offer: ExternalOffer) => isFavorite(offer._id));

  const externalOffersEmptyState = !externalOffers || !availableExternalOffers?.length;

  const tabsArray = [
    <Tab
      key="offersAll"
      aria-controls="tabpanel-all"
      id="tab-all"
      label={formatMessage({ id: 'offersAll' })}
      selected={selectedTab === dynamicAvailableTabs.AllOffers}
    />,
    enableExternalOffers ? (
      <Tab
        key="offersExternal"
        aria-controls="tabpanel-externals"
        id="tab-externals"
        label={formatMessage({ id: 'offersExternal' })}
        selected={selectedTab === dynamicAvailableTabs.ExternalOffers}
      />
    ) : null,
    isAuthenticated() ? (
      <Tab
        key="offersFavorites"
        aria-controls="tabpanel-favorites"
        id="tab-favorites"
        label={formatMessage({ id: 'offersFavorites' })}
        selected={selectedTab === dynamicAvailableTabs.Favorites}
      />
    ) : null,
  ].filter(el => !!el);

  const shouldShowLoaderTiles = offersLoading && !offersAndTiles?.length;
  const shouldShowLoaderTabs = offersLoading && !tabsArray.length;

  return (
    <LoyaltyIncentivesContainer>
      {shouldRenderOfferUpdatedMessage && !loadingNearbyStore && (
        <InlineAlert message={offerUpdatedMessage} icon="checkCircle" />
      )}
      <SkeletonTheme baseColor="#0000000a" highlightColor="#0000001a">
        {((enableFavoriteOffers && isAuthenticated()) ||
          (enableExternalOffers && !isAuthenticated())) &&
          !(selectedIncentive && isMobile) && (
            <TabContainer>
              {shouldShowLoaderTabs ? (
                Boolean(user) && (
                  <OffersSkeletonTabs>
                    <Skeleton width={'3rem'} height={'1.5rem'} />
                    <Skeleton width={'3rem'} height={'1.5rem'} />
                    <Skeleton width={'3rem'} height={'1.5rem'} />
                  </OffersSkeletonTabs>
                )
              ) : (
                <TabList selectedIndex={selectedTab} onChange={handleTabChange}>
                  {tabsArray as JSX.Element[]}
                </TabList>
              )}
            </TabContainer>
          )}

        {enableFavoriteOffers &&
          selectedTab === dynamicAvailableTabs.Favorites &&
          !selectedIncentive && (
            <TabDisclaimer>
              <Icon icon={'info'} color={'tertiary'} width="22px" aria-hidden />
              {formatMessage({ id: 'offersFavoritesDisclaimer' })}
            </TabDisclaimer>
          )}

        {enableLoyaltyPromoCodes &&
        selectedTab !== dynamicAvailableTabs.ExternalOffers &&
        !isExternalOffer(selectedIncentive) ? (
          !(selectedIncentive && isMobile) && shouldShowLoaderTiles ? (
            <OffersSkeletonPromoCode>
              <Skeleton height={'2.5rem'} width={'2.5rem'} circle />
              <Skeleton height={'1.25rem'} width={'180px'} />
            </OffersSkeletonPromoCode>
          ) : (
            <LoyaltyPromoCode onPromoCodeRedeemSuccess={onPromoCodeRedeemSuccess} />
          )
        ) : null}

        <LoyaltyIncentivesGrid
          selectedIncentive={selectedIncentive}
          refetch={handleRefetch}
          goBack={state?.backRoute || `${formatMessage({ id: 'routes.loyaltyHome' })}/${tabPath}`}
          engineIncentivesMap={{}}
          offersInCooldown={loyaltyOffersInCooldown}
          challengeIncentiveSelected={challengeIncentiveSelected}
          offersLoadingState={offersLoading}
        >
          <IncentiveItemsContainer>
            {Boolean(offersInCart?.length) && !shouldHideSelectedOffer && (
              <OffersInCart
                offers={offersInCart}
                handleClick={handleClick}
                handleRemove={handleRemove}
                favoriteEnabled={enableFavoriteOffers && isAuthenticated()}
              />
            )}
            {!questsLoading &&
              challengeIncentives?.map(challengeIncentive => {
                const parsedChallengeIncentive = parseIncentiveData(challengeIncentive);

                // Do not show the challenge Incentive on Favorites tab
                // except when it is favorite
                return (
                  (selectedTab !== dynamicAvailableTabs.Favorites ||
                    (selectedTab === dynamicAvailableTabs.Favorites &&
                      isFavorite(challengeIncentive._id || ''))) && (
                    <IncentiveCard
                      key={challengeIncentive.loyaltyEngineId}
                      isQuestChallengeIcentive
                      incentiveData={parsedChallengeIncentive}
                      handleClick={handleClick}
                      favoriteEnabled={enableFavoriteOffers && isAuthenticated()}
                      favorited={enableFavoriteOffers && isFavorite(challengeIncentive._id || '')}
                      favoriteLoading={isOfferLoading(challengeIncentive._id ?? '')}
                      handleToggleFavorite={toggleFavorite}
                    ></IncentiveCard>
                  )
                );
              })}
            {marketingTileTop &&
              selectedTab !== dynamicAvailableTabs.Favorites &&
              selectedTab !== dynamicAvailableTabs.ExternalOffers &&
              !offersLoading && (
                <Tile
                  data-testid="top-marketing-tile"
                  tile={marketingTileTop}
                  size={TileSize.HALF}
                  buttonPosition={HorizontalPosition.RIGHT}
                  hasBorder={false}
                  additionalPadding={`0.5rem`}
                />
              )}

            {shouldShowLoaderTiles ? (
              <>
                {new Array(6).fill('').map((_, index) => (
                  <IncentiveCardSkeleton key={index} />
                ))}
              </>
            ) : (
              offersAndTiles
            )}

            {((externalOffers && selectedTab === dynamicAvailableTabs.ExternalOffers) ||
              (enableFavoriteOffers && selectedTab === dynamicAvailableTabs.Favorites)) &&
              !offersLoading &&
              availableExternalOffers}

            {marketingTileBottom &&
              selectedTab !== dynamicAvailableTabs.Favorites &&
              selectedTab !== dynamicAvailableTabs.ExternalOffers &&
              !shouldShowLoaderTiles && (
                <Tile
                  data-testid="bottom-marketing-tile"
                  tile={marketingTileBottom}
                  size={TileSize.HALF}
                  buttonPosition={HorizontalPosition.RIGHT}
                  hasBorder={false}
                  additionalPadding={`0.5rem`}
                />
              )}
            {!offersAndTiles?.length &&
              !shouldShowLoaderTiles &&
              selectedTab === dynamicAvailableTabs.AllOffers && <OffersEmptyState />}
            {enableFavoriteOffers &&
              selectedTab === dynamicAvailableTabs.Favorites &&
              favoritesEmptyState && <FavoritesEmptyState />}
            {enableExternalOffers &&
              selectedTab === dynamicAvailableTabs.ExternalOffers &&
              externalOffersEmptyState && <ExternalOffersEmptyState />}
            {Boolean(offers?.length) && <LoyaltyEndOfOffers />}
          </IncentiveItemsContainer>
        </LoyaltyIncentivesGrid>

        {enableInvitationCode && loyaltyUser && (
          <ModalQuestUnlockedIncentive
            onChangeUnlockedIncentivesModalVisibilityStatus={
              onChangeUnlockedIncentivesModalVisibilityStatus
            }
            unlockedIncentivesModalVisibilityStatus={unlockedIncentivesModalVisibilityStatus}
          />
        )}
      </SkeletonTheme>
    </LoyaltyIncentivesContainer>
  );
};

export const LoyaltyOffers = LoyaltyOffersBase;
